/**
 * AdminPill — floating dev panel visible only to admins.
 * Shows: version · env · stripe mode (with live/sandbox toggle)
 * Expands to show last deploy timestamps per environment.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Animated,
  Platform,
} from 'react-native';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigation } from '@react-navigation/native';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { VERSION, BUILD_DATE } from '../config/version';

const isWeb = Platform.OS === 'web';
const ENV = process.env.EXPO_PUBLIC_ENV || 'development';
const ENV_SHORT = ENV === 'production' ? 'prod' : ENV === 'staging' ? 'stg' : 'dev';

const ENV_COLOR = {
  production: '#EF4444',
  staging: '#F59E0B',
  development: '#10B981',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const AdminPill = () => {
  const { user } = useAuth();
  const navigation = useNavigation();
  const [expanded, setExpanded] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [deploys, setDeploys] = useState(null);
  const [toggling, setToggling] = useState(false);

  const heightAnim = useRef(new Animated.Value(0)).current;

  // All hooks must be called before any conditional return
  useEffect(() => {
    // Skip Firestore subscriptions for non-admins
    if (!user?.isAdmin) return;
    const unsubSettings = onSnapshot(doc(db, 'settings', 'app'), snap => {
      if (snap.exists()) setAppSettings(snap.data());
    });
    const unsubDeploys = onSnapshot(doc(db, 'settings', 'deploys'), snap => {
      if (snap.exists()) setDeploys(snap.data());
    });
    return () => { unsubSettings(); unsubDeploys(); };
  }, []);

  useEffect(() => {
    Animated.spring(heightAnim, {
      toValue: expanded ? 1 : 0,
      friction: 12,
      tension: 80,
      useNativeDriver: false,
    }).start();
  }, [expanded]);

  // Only render for admins — after all hooks are declared
  if (!user?.isAdmin) return null;

  const stripeMode = appSettings?.stripeMode || 'sandbox';
  const envColor = ENV_COLOR[ENV] || '#6B7280';

  const toggleStripeMode = async () => {
    if (toggling) return;
    setToggling(true);
    const next = stripeMode === 'live' ? 'sandbox' : 'live';
    try {
      await updateDoc(doc(db, 'settings', 'app'), { stripeMode: next });
    } catch (e) {
      console.warn('Failed to toggle stripe mode:', e.message);
    } finally {
      setToggling(false);
    }
  };

  const pillBg = '#111827';
  const pillText = '#F9FAFB';
  const pillMuted = '#9CA3AF';
  const pillSurface = '#1F2937';

  const expandedMaxHeight = heightAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 260],
  });

  return (
    <View style={styles.container}>
      <View style={[styles.pill, { backgroundColor: pillBg }]}>
        {/* Collapsed row */}
        <Pressable
          onPress={() => setExpanded(!expanded)}
          style={styles.collapsedRow}
          accessibilityLabel="Toggle admin panel"
        >
          <View style={[styles.envDot, { backgroundColor: envColor }]} />
          <Text style={[styles.pillLabel, { color: pillText }]}>
            v{VERSION}
          </Text>
          <Text style={[styles.pillSep, { color: pillMuted }]}> · </Text>
          <Text style={[styles.pillLabel, { color: envColor }]}>{ENV_SHORT}</Text>
          <Text style={[styles.pillSep, { color: pillMuted }]}> · </Text>
          <Text style={[styles.pillLabel, { color: stripeMode === 'live' ? '#EF4444' : '#10B981' }]}>
            {stripeMode}
          </Text>
          <Text style={[styles.chevron, { color: pillMuted }]}>{expanded ? ' ▲' : ' ▼'}</Text>
        </Pressable>

        {/* Expanded panel */}
        <Animated.View style={[styles.expandedWrap, { maxHeight: expandedMaxHeight, overflow: 'hidden' }]}>
          <View style={[styles.divider, { backgroundColor: pillSurface }]} />

          {/* Version + build */}
          <Row label="Version" value={`v${VERSION}`} muted={pillMuted} text={pillText} />
          <Row label="Build date" value={BUILD_DATE} muted={pillMuted} text={pillText} />
          <Row label="Environment" value={ENV} muted={pillMuted} text={envColor} bold />

          {/* Stripe mode toggle */}
          <View style={styles.row}>
            <Text style={[styles.rowLabel, { color: pillMuted }]}>Stripe mode</Text>
            <Pressable
              onPress={toggleStripeMode}
              style={[styles.modeToggle, {
                backgroundColor: stripeMode === 'live' ? '#EF444430' : '#10B98130',
                borderColor: stripeMode === 'live' ? '#EF4444' : '#10B981',
              }]}
            >
              <Text style={[styles.modeToggleText, { color: stripeMode === 'live' ? '#EF4444' : '#10B981' }]}>
                {toggling ? '...' : stripeMode === 'live' ? '● live' : '● sandbox'}
              </Text>
            </Pressable>
          </View>

          <View style={[styles.divider, { backgroundColor: pillSurface, marginVertical: 8 }]} />

          {/* Last deploys */}
          <Text style={[styles.sectionLabel, { color: pillMuted }]}>LAST DEPLOYS</Text>
          {['production', 'staging', 'development'].map(e => {
            const d = deploys?.[e];
            return (
              <View key={e} style={styles.row}>
                <Text style={[styles.rowLabel, { color: ENV_COLOR[e] || pillMuted }]}>{e.slice(0, 4)}</Text>
                <Text style={[styles.rowValue, { color: pillText, fontSize: 11 }]}>
                  {d ? `v${d.version}  ${formatDate(d.deployedAt)}` : '—'}
                </Text>
              </View>
            );
          })}

          {/* Admin screen link */}
          <Pressable
            onPress={() => { setExpanded(false); navigation.navigate('Admin'); }}
            style={[styles.adminLink, { backgroundColor: pillSurface }]}
          >
            <Text style={[styles.adminLinkText, { color: '#C75B7A' }]}>Open Admin screen →</Text>
          </Pressable>
        </Animated.View>
      </View>
    </View>
  );
};

const Row = ({ label, value, muted, text, bold }) => (
  <View style={styles.row}>
    <Text style={[styles.rowLabel, { color: muted }]}>{label}</Text>
    <Text style={[styles.rowValue, { color: text, fontWeight: bold ? '700' : '400' }]}>{value}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    left: 16,
    zIndex: 999,
    pointerEvents: 'box-none',
  },
  pill: {
    borderRadius: 14,
    overflow: 'hidden',
    minWidth: 220,
    maxWidth: 300,
    ...Platform.select({
      web: { boxShadow: '0 4px 24px rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)' },
      default: { elevation: 8 },
    }),
  },
  collapsedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  envDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 7,
  },
  pillLabel: { fontSize: 12, fontWeight: '600' },
  pillSep: { fontSize: 12 },
  chevron: { fontSize: 10, marginLeft: 2 },
  expandedWrap: { paddingHorizontal: 14, paddingBottom: 10 },
  divider: { height: 1, marginVertical: 6, borderRadius: 1 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginVertical: 3 },
  rowLabel: { fontSize: 11, fontWeight: '500' },
  rowValue: { fontSize: 12 },
  sectionLabel: { fontSize: 10, fontWeight: '700', letterSpacing: 0.8, marginBottom: 4, marginTop: 2 },
  modeToggle: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  modeToggleText: { fontSize: 11, fontWeight: '700' },
  adminLink: {
    marginTop: 10,
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
  },
  adminLinkText: { fontSize: 12, fontWeight: '700' },
});

export default AdminPill;
